import React, { useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Stack, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JSMCreateSurveyData } from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-survey';
import { SurveyBuilder } from '@atlassian/survey-builder';

const styles = cssMap({
	container: {
		position: 'relative',
	},
	surveyWrapper: {
		overflow: 'hidden',
		transition: 'max-height 0.3s ease',
	},
	collapsed: {
		height: '380px',
	},
	expanded: {
		height: 'auto',
	},
	showMoreWrapper: {
		justifyContent: 'center',
		paddingBottom: token('space.250'),
		position: 'relative',
	},
	fade: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		pointerEvents: 'none',
		width: '100%',
		height: '50%',
	},
	showMore: {
		display: 'flex',
		minHeight: '24px',
		paddingTop: 0,
		paddingBottom: 0,
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
});

const showMoreMessage = defineMessage({
	id: 'conversation-assistant-ui-components.survey-preview-card-show-more',
	defaultMessage: 'Show more',
	description: 'Button label to expand the survey builder to show full content',
});

export const JsmCreateSurveyMainContent = ({
	selectedFormQuestions: { title, description, questions },
}: Omit<JSMCreateSurveyData, 'portalId'>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const handleToggle = () => setIsExpanded((prev) => !prev);

	return (
		<Stack xcss={styles.container}>
			<Box xcss={cx(styles.surveyWrapper, isExpanded ? styles.expanded : styles.collapsed)}>
				<SurveyBuilder
					title={title}
					description={description}
					questions={questions}
					testId="conversation-assistant.jsm-create-survey-preview-card.survey-builder"
				/>
			</Box>
			{!isExpanded && (
				<>
					<Box
						xcss={styles.fade}
						aria-hidden="true"
						style={{
							background: `linear-gradient(to top, var(--ds-surface, #ffffff), transparent)`,
						}}
					/>
					<Inline xcss={styles.showMoreWrapper}>
						<Pressable
							xcss={styles.showMore}
							onClick={handleToggle}
							testId="conversation-assistant.jsm-create-survey-preview-card.show-more-button"
							type="button"
						>
							<Text color="color.text.subtle" size="small" weight="medium">
								{formatMessage(showMoreMessage)}
							</Text>
						</Pressable>
					</Inline>
				</>
			)}
		</Stack>
	);
};
